<template>
  <div class="program-chair-dashboard pa-10">

    <!-- ── Header ── -->
    <div class="d-flex flex-wrap justify-space-between align-center mb-7 gap-3">
      <div>
        <div class="dash-page-title">Program Chairperson Dashboard</div>
        <div class="dash-page-sub mt-1">Class Management &amp; Student Monitoring</div>
      </div>
      <div class="d-flex align-center" style="gap: 12px;">
        <v-select
          v-model="selectedBatchId"
          :items="batches"
          item-title="name"
          item-value="id"
          density="compact"
          variant="outlined"
          hide-details
          class="batch-select"
          style="min-width: 200px;"
        />
        <div class="date-chip">{{ currentDate }}</div>
        <v-btn
          color="#10b981"
          variant="flat"
          prepend-icon="mdi-download-outline"
          class="text-white"
          rounded="lg"
          @click="showReportsDialog = true"
        >
          Generate Reports
        </v-btn>
      </div>
    </div>

    <GenerateReportsDialog
      v-if="showReportsDialog"
      @close="showReportsDialog = false"
    />

    <!-- ── Stat Cards ── -->
    <v-row class="mb-5">
      <v-col cols="12" sm="6">
        <div class="stat-card stat-card--teal rounded-xl pa-5">
          <div class="d-flex justify-space-between align-center">
            <div class="stat-icon-box stat-icon-box--teal rounded-xl pa-3">
              <v-icon size="32" color="#0d9488">mdi-account-group-outline</v-icon>
            </div>
            <div class="text-right">
              <div class="stat-number stat-number--teal">{{ stats.total_classes }}</div>
              <div class="stat-label stat-label--teal">Total Classes</div>
              <div class="stat-sub">Active program sections</div>
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="12" sm="6">
        <div class="stat-card stat-card--blue rounded-xl pa-5">
          <div class="d-flex justify-space-between align-center">
            <div class="stat-icon-box stat-icon-box--blue rounded-xl pa-3">
              <v-icon size="32" color="#2563eb">mdi-account-outline</v-icon>
            </div>
            <div class="text-right">
              <div class="stat-number stat-number--blue">{{ stats.total_students }}</div>
              <div class="stat-label stat-label--blue">Total Students</div>
              <div class="stat-sub">Enrolled this batch</div>
            </div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="6">
        <div class="stat-card stat-card--green rounded-xl pa-5">
          <div class="d-flex justify-space-between align-center">
            <div class="stat-icon-box stat-icon-box--green rounded-xl pa-3">
              <v-icon size="32" color="#059669">mdi-briefcase-check-outline</v-icon>
            </div>
            <div class="text-right">
              <div class="stat-number stat-number--green">{{ stats.deployed }}</div>
              <div class="stat-label stat-label--green">Deployed</div>
              <div class="stat-sub">Currently in training</div>
            </div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="6">
        <div class="stat-card stat-card--amber rounded-xl pa-5">
          <div class="d-flex justify-space-between align-center">
            <div class="stat-icon-box stat-icon-box--amber rounded-xl pa-3">
              <v-icon size="32" color="#d97706">mdi-check-decagram-outline</v-icon>
            </div>
            <div class="text-right">
              <div class="stat-number stat-number--amber">{{ stats.completed }}</div>
              <div class="stat-label stat-label--amber">Completed</div>
              <div class="stat-sub">Finished OJT</div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- ── Class List + Distribution ── -->
    <v-row align="stretch">
      <v-col cols="12" md="8">
        <div class="dash-card rounded-xl pa-6 h-100">
          <div class="d-flex justify-space-between align-center mb-5">
            <div>
              <div class="card-title">Class List Overview</div>
              <div class="card-sub mt-1">Student deployment status per program</div>
            </div>
            <a class="view-all-link" @click="$router.push('/program-chair/monitor-students')">
              View All Students →
            </a>
          </div>

          <div v-if="loading" class="py-4">
            <v-skeleton-loader v-for="n in 4" :key="n" type="list-item-two-line" class="mb-2" />
          </div>

          <template v-else-if="classList.length">
            <div
              v-for="(cls, idx) in classList"
              :key="cls.program"
            >
              <div class="class-row">
                <div class="class-info flex-grow-1">
                  <div class="class-name">{{ cls.program }}</div>
                  <div class="class-sub">{{ cls.total }} students enrolled</div>
                  <div class="class-bar-wrap mt-2">
                    <div class="class-bar">
                      <div
                        class="class-bar-fill"
                        :style="{ width: cls.deployedPct + '%', backgroundColor: barColor(cls.deployedPct) }"
                      ></div>
                    </div>
                  </div>
                </div>
                <div class="class-stats">
                  <div class="class-count">{{ cls.total }} students</div>
                  <div class="class-deployed" :style="{ color: barColor(cls.deployedPct) }">
                    {{ cls.deployed }} deployed
                  </div>
                </div>
              </div>
              <v-divider v-if="idx < classList.length - 1" />
            </div>
          </template>

          <div v-else class="text-center text-grey py-8">
            No class data available.
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="4">
        <div class="dash-card rounded-xl pa-6 h-100">
          <div class="card-title mb-1">Student Distribution by Program</div>
          <div class="card-sub mb-5">Enrollment breakdown across programs</div>
          <div class="chart-wrap chart-wrap--doughnut">
            <canvas ref="distributionChart"></canvas>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Recent Activity -->
    <v-row class="mb-5">
      <v-col cols="12">
        <div class="dash-card rounded-xl pa-6">
          <div class="d-flex justify-space-between align-center mb-5">
            <div>
              <div class="card-title">Recent Activity</div>
              <div class="card-sub">Latest system events</div>
            </div>
            <router-link to="/program-chair/activity-log" class="activity-view-all-link">View All →</router-link>
          </div>
          <div v-if="activityLoading" class="d-flex justify-center py-6">
            <v-progress-circular indeterminate color="#10b981" size="32" />
          </div>
          <div v-else-if="!recentActivity.length" class="text-center py-6" style="color: #9ca3af; font-size: 13px">
            No recent activity
          </div>
          <div v-else>
            <div
              v-for="(item, idx) in recentActivity"
              :key="idx"
              class="activity-item rounded-xl pa-4"
              :class="idx < recentActivity.length - 1 ? 'mb-3' : ''"
              :style="getActivityBadgeStyle(item.action)"
            >
              <div class="d-flex align-start ga-3">
                <div class="activity-icon-box rounded-lg pa-2 flex-shrink-0" :style="{ background: getActivityConfig(item.action).iconBg }">
                  <v-icon :color="getActivityColor(item.action)" size="18">{{ getActivityIcon(item.action) }}</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex justify-space-between align-start">
                    <div class="activity-title">{{ item.description || item.action }}</div>
                    <v-chip size="x-small" :color="getActivityColor(item.action)" variant="tonal" class="ml-2 flex-shrink-0">{{ getActivityConfig(item.action).label }}</v-chip>
                  </div>
                  <div class="activity-desc">By {{ item.performed_by_name || 'System' }}</div>
                  <div class="activity-time">{{ formatActivityTime(item.created_at) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import Chart from "chart.js/auto";
import GenerateReportsDialog from "@/components/Dialogs/Forms/GenerateReportsDialog.vue";

export default {
  name: "ProgramChairDashboard",
  components: { GenerateReportsDialog },

  data() {
    return {
      showReportsDialog: false,
      loading: false,
      charts: {},
      recentActivity: [],
      activityLoading: false,
      stats: { total_classes: 0, total_students: 0, deployed: 0, completed: 0 },
      classList: [],
      distributionData: [],
      batches: [],
      selectedBatchId: null,
    };
  },

  computed: {
    currentDate() {
      return new Date().toLocaleDateString("en-US", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      });
    },
  },

  watch: {
    selectedBatchId(val) {
      if (val) this.loadStudentData(val);
    },
  },

  mounted() {
    this.fetchData();
    this.fetchRecentActivity();
  },

  beforeUnmount() {
    Object.values(this.charts).forEach((c) => c && c.destroy());
  },

  methods: {
    async fetchData() {
      try {
        const batchRes = await this.axiosCall("/program/getProgramsBatchByInstitute", "GET");
        if (!batchRes?.data?.length) return;
        this.batches = batchRes.data;
        this.selectedBatchId = batchRes.data[0].id;
      } catch (e) {
        console.error("Failed to load batches", e);
      }
    },

    async loadStudentData(batchId) {
      this.loading = true;
      try {
        const res = await this.axiosCall(`/student/program-chair-dashboard?batch=${batchId}`, "GET");
        if (!res?.data) return;
        const d = res.data;
        this.stats = { ...this.stats, ...d.stats };
        this.classList = d.classList || [];
        this.distributionData = d.chart_data || [];
      } catch (e) {
        console.error("Failed to load program chair dashboard", e);
      } finally {
        this.loading = false;
        this.$nextTick(() => this.initDistributionChart());
      }
    },

    initDistributionChart() {
      if (this.charts.distribution) this.charts.distribution.destroy();
      if (!this.$refs.distributionChart) return;

      this.charts.distribution = new Chart(this.$refs.distributionChart, {
        type: "doughnut",
        data: {
          labels: this.distributionData.map((d) => `${d.label} (${d.value})`),
          datasets: [{
            data: this.distributionData.map((d) => d.value),
            backgroundColor: this.distributionData.map((d) => d.color),
            borderWidth: 3,
            borderColor: "#fff",
            hoverOffset: 6,
          }],
        },
        options: {
          cutout: "68%",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                padding: 18,
                font: { size: 12 },
                color: "#6b7280",
              },
            },
          },
        },
      });
    },

    barColor(pct) {
      if (pct >= 75) return "#10b981";
      if (pct >= 40) return "#f59e0b";
      return "#ef4444";
    },

    async fetchRecentActivity() {
      this.activityLoading = true;
      try {
        const res = await this.axiosCall('/system-logs?page=1&limit=5', 'GET');
        if (res?.data?.data) this.recentActivity = res.data.data;
      } catch (e) {
        console.error('Failed to load recent activity', e);
      } finally {
        this.activityLoading = false;
      }
    },
    getActivityConfig(action) {
      const map = {
        USER_LOGIN:              { color: '#10b981', iconBg: '#d1fae5', icon: 'mdi-login',                   label: 'Login'      },
        USER_LOGOUT:             { color: '#6b7280', iconBg: '#f3f4f6', icon: 'mdi-logout',                  label: 'Logout'     },
        BATCH_CREATED:           { color: '#8b5cf6', iconBg: '#ede9fe', icon: 'mdi-layers-plus',             label: 'Batch'      },
        STUDENT_ENDORSED:        { color: '#3b82f6', iconBg: '#dbeafe', icon: 'mdi-account-arrow-right',     label: 'Endorsed'   },
        STUDENT_DEPLOYED:        { color: '#10b981', iconBg: '#d1fae5', icon: 'mdi-briefcase-check-outline', label: 'Deployed'   },
        STUDENT_PULLED_OUT:      { color: '#ef4444', iconBg: '#fee2e2', icon: 'mdi-account-minus-outline',   label: 'Pulled Out' },
        STUDENT_REASSIGNED:      { color: '#f59e0b', iconBg: '#fef3c7', icon: 'mdi-account-switch-outline',  label: 'Reassigned' },
        REQUIREMENT_SUBMITTED:   { color: '#3b82f6', iconBg: '#dbeafe', icon: 'mdi-file-upload-outline',     label: 'Submitted'  },
        REQUIREMENT_APPROVED:    { color: '#10b981', iconBg: '#d1fae5', icon: 'mdi-file-check-outline',      label: 'Approved'   },
        REQUIREMENT_DISAPPROVED: { color: '#ef4444', iconBg: '#fee2e2', icon: 'mdi-file-cancel-outline',     label: 'Returned'   },
        ATTENDANCE_SCANNED:      { color: '#06b6d4', iconBg: '#cffafe', icon: 'mdi-qrcode-scan',             label: 'Attendance' },
      };
      return map[action] || { color: '#6b7280', iconBg: '#f3f4f6', icon: 'mdi-information-outline', label: action };
    },
    getActivityColor(action) { return this.getActivityConfig(action).color; },
    getActivityIcon(action)  { return this.getActivityConfig(action).icon; },
    getActivityBadgeStyle(action) {
      const c = this.getActivityConfig(action);
      return { background: c.iconBg, border: `1px solid ${c.color}22` };
    },
    formatActivityTime(dateStr) {
      if (!dateStr) return '';
      const diff  = Date.now() - new Date(dateStr).getTime();
      const mins  = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days  = Math.floor(diff / 86400000);
      if (mins  < 1)  return 'just now';
      if (mins  < 60) return `${mins}m ago`;
      if (hours < 24) return `${hours}h ago`;
      return `${days}d ago`;
    },
  },
};
</script>

<style scoped>
.program-chair-dashboard { min-height: 100vh; }

/* ── Header ── */
.dash-page-title {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.2;
}
.dash-page-sub {
  font-size: 14px;
  color: #6b7280;
}
.date-chip {
  font-size: 13.5px;
  font-weight: 600;
  color: #374151;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 8px 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.batch-select :deep(.v-field) {
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  background: #fff;
  --v-field-border-opacity: 1;
}
.batch-select :deep(.v-field__input),
.batch-select :deep(.v-select__selection-text) {
  font-size: 13.5px;
  font-weight: 600;
  color: #374151;
}
.batch-select :deep(.v-field__outline__start),
.batch-select :deep(.v-field__outline__notch),
.batch-select :deep(.v-field__outline__end) {
  border-color: #E2E8F0 !important;
}
.batch-select :deep(.v-field__outline__start) { border-radius: 10px 0 0 10px; }
.batch-select :deep(.v-field__outline__end)   { border-radius: 0 10px 10px 0; }
.batch-select :deep(.v-field--focused .v-field__outline__start),
.batch-select :deep(.v-field--focused .v-field__outline__notch),
.batch-select :deep(.v-field--focused .v-field__outline__end) {
  border-color: #E2E8F0 !important;
}

/* ── Stat Cards ── */
.stat-card {
  border: 1px solid transparent;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s;
  height: 100%;
}
.stat-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.13); }
.stat-card--teal  { background: #f0fdfa; border-color: #99f6e4; }
.stat-card--blue  { background: #eff6ff; border-color: #bfdbfe; }
.stat-card--green { background: #f0fdf4; border-color: #a7f3d0; }
.stat-card--amber { background: #fffbeb; border-color: #fde68a; }

.stat-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.stat-icon-box--teal  { background: #99f6e4; }
.stat-icon-box--blue  { background: #bfdbfe; }
.stat-icon-box--green { background: #a7f3d0; }
.stat-icon-box--amber { background: #fde68a; }

.stat-number {
  font-size: 40px;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -1.5px;
}
.stat-number--teal  { color: #0d9488; }
.stat-number--blue  { color: #2563eb; }
.stat-number--green { color: #059669; }
.stat-number--amber { color: #d97706; }

.stat-label {
  font-size: 13px;
  font-weight: 600;
  margin-top: 6px;
}
.stat-label--teal  { color: #0d9488; }
.stat-label--blue  { color: #2563eb; }
.stat-label--green { color: #059669; }
.stat-label--amber { color: #d97706; }

.stat-sub {
  font-size: 11.5px;
  color: #9ca3af;
  margin-top: 3px;
}

/* ── Dash Cards ── */
.dash-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
.card-sub {
  font-size: 12.5px;
  color: #9ca3af;
}
.view-all-link {
  font-size: 13px;
  color: #10b981;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
}
.view-all-link:hover { text-decoration: underline; }

/* ── Class List ── */
.class-row {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 16px 0;
}
.class-name {
  font-size: 14.5px;
  font-weight: 700;
  color: #111827;
}
.class-sub {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}
.class-bar-wrap { max-width: 420px; }
.class-bar {
  height: 8px;
  background: #e5e7eb;
  border-radius: 99px;
  overflow: hidden;
}
.class-bar-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.4s ease;
}
.class-stats {
  text-align: right;
  flex-shrink: 0;
  min-width: 100px;
}
.class-count {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.class-deployed {
  font-size: 12px;
  font-weight: 600;
  margin-top: 2px;
}

/* ── Chart ── */
.chart-wrap {
  position: relative;
  width: 100%;
  height: 230px;
}
.chart-wrap--doughnut { height: 260px; }

.activity-view-all-link { font-size: 13px; font-weight: 600; color: #10b981; text-decoration: none; }
.activity-view-all-link:hover { text-decoration: underline; }
.activity-item { transition: box-shadow 0.15s; }
.activity-icon-box { display: inline-flex; }
.activity-title { font-size: 13px; font-weight: 600; color: #111827; }
.activity-desc  { font-size: 12px; color: #6b7280; margin-top: 2px; }
.activity-time  { font-size: 11px; color: #9ca3af; margin-top: 4px; }
</style>
